import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Input, Button, CustomSelect, Label, FormField, FormError, Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@redpanda/ui';
import { useCustomers } from '../stores/useCustomerStore';
import { useTranslation } from 'react-i18next';

interface CustomerDetailDialogProps {
  isOpen: boolean;
  onClose: () => void;
  customerId: string | null;
  onUpdate?: () => void;
}

interface FormData {
  vipLevel: string;
  tags: string;
  note: string;
}

const CustomerDetailDialog: React.FC<CustomerDetailDialogProps> = ({
  isOpen,
  onClose,
  customerId,
  onUpdate,
}) => {
  const { t } = useTranslation(['pages', 'translation']);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<FormData>();

  const {
    customerDetails,
    isLoadingDetail,
    fetchCustomerDetail,
    updateCustomer,
  } = useCustomers();

  const watchedVipLevel = watch('vipLevel', 'REGULAR');

  // 取得快取的客戶詳情
  const customerDetail = customerId ? customerDetails[customerId] : null;

  useEffect(() => {
    if (isOpen && customerId) {
      // 先從快取取得資料，然後背景更新
      fetchCustomerDetail(customerId);
    }
  }, [isOpen, customerId, fetchCustomerDetail]);

  useEffect(() => {
    if (customerDetail) {
      // 設置表單初始值
      setValue('vipLevel', customerDetail.vipLevel || 'REGULAR');
      setValue('tags', (customerDetail.tags || []).join(', '));
      setValue('note', customerDetail.note || '');
    }
  }, [customerDetail, setValue]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleBackgroundClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleFormSubmit = (data: FormData) => {
    if (!customerId) return;

    // 將逗號分隔的標籤字串轉換為陣列
    const tagsArray = data.tags
      .split(',')
      .map(tag => tag.trim())
      .filter(tag => tag.length > 0);

    // 立即關閉（樂觀更新在 store 處理）
    if (onUpdate) onUpdate();
    handleClose();

    updateCustomer(customerId, {
      vipLevel: data.vipLevel,
      tags: tagsArray,
      note: data.note,
    }).catch(() => {
      // 錯誤處理與 UI 回滾在 store 中完成
    });
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { label: string; className: string }> = {
      PENDING: { label: t('bookingStatus.PENDING', { ns: 'translation' }), className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400' },
      CONFIRMED: { label: t('bookingStatus.CONFIRMED', { ns: 'translation' }), className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400' },
      COMPLETED: { label: t('bookingStatus.COMPLETED', { ns: 'translation' }), className: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' },
      CANCELLED: { label: t('bookingStatus.CANCELLED', { ns: 'translation' }), className: 'bg-gray-100 text-gray-800 dark:bg-slate-700 dark:text-gray-300' },
    };

    const statusInfo = statusMap[status] || { label: status, className: 'bg-gray-100 text-gray-800' };

    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${statusInfo.className}`}>
        {statusInfo.label}
      </span>
    );
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (!isOpen) return null;

  const vipLevelOptions = [
    { value: 'REGULAR', label: t('dialogs.customer.regular', { ns: 'pages' }) },
    { value: 'VIP', label: t('dialogs.customer.vip', { ns: 'pages' }) },
    { value: 'SVIP', label: t('dialogs.customer.svip', { ns: 'pages' }) },
  ];

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4"
      onMouseDown={handleBackgroundClick}
    >
      <div
        className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 標題列 */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-slate-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            {t('dialogs.customer.title', { ns: 'pages' })}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* 內容 */}
        {!customerDetail ? (
          <div className="p-6 text-center text-gray-500 dark:text-gray-400">
            {isLoadingDetail ? t('dialogs.customer.loading', { ns: 'pages' }) : t('dialogs.customer.loadError', { ns: 'pages' })}
          </div>
        ) : (
          <div className="p-6 space-y-6">
            {/* 基本資料 */}
            <div className="bg-gray-50 dark:bg-slate-700 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                {t('dialogs.customer.basicInfo', { ns: 'pages' })}
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('dialogs.customer.name', { ns: 'pages' })}</p>
                  <p className="text-base font-medium text-gray-900 dark:text-white">
                    {customerDetail.name}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('dialogs.customer.phone', { ns: 'pages' })}</p>
                  <p className="text-base font-medium text-gray-900 dark:text-white">
                    {customerDetail.phone}
                  </p>
                </div>
                {customerDetail.email && (
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{t('dialogs.customer.email', { ns: 'pages' })}</p>
                    <p className="text-base font-medium text-gray-900 dark:text-white">
                      {customerDetail.email}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* 消費統計 */}
            <div className="bg-gray-50 dark:bg-slate-700 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                {t('dialogs.customer.consumptionStats', { ns: 'pages' })}
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('dialogs.customer.totalSpent', { ns: 'pages' })}</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">
                    ${Math.floor(Number(customerDetail.totalSpent))}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('dialogs.customer.visitCount', { ns: 'pages' })}</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">
                    {customerDetail.visitCount}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('dialogs.customer.lastVisit', { ns: 'pages' })}</p>
                  <p className="text-base font-medium text-gray-900 dark:text-white">
                    {customerDetail.lastVisit ? formatDateTime(customerDetail.lastVisit) : '-'}
                  </p>
                </div>
              </div>
            </div>

            {/* 店家管理資料 */}
            <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('dialogs.customer.managementData', { ns: 'pages' })}
              </h3>

              {/* 會員等級 */}
              <FormField>
                <Label htmlFor="vipLevel">{t('dialogs.customer.memberLevel', { ns: 'pages' })}</Label>
                <input
                  type="hidden"
                  {...register('vipLevel')}
                />
                <CustomSelect
                  id="vipLevel"
                  value={watchedVipLevel}
                  onChange={(value) => setValue('vipLevel', value)}
                  options={vipLevelOptions}
                />
              </FormField>

              {/* 標籤 */}
              <FormField>
                <Label htmlFor="tags">{t('dialogs.customer.tags', { ns: 'pages' })}</Label>
                <Input
                  id="tags"
                  {...register('tags')}
                  placeholder={t('dialogs.customer.tagsPlaceholder', { ns: 'pages' })}
                />
              </FormField>

              {/* 備註 */}
              <FormField>
                <Label htmlFor="note">{t('dialogs.customer.storeNote', { ns: 'pages' })}</Label>
                <textarea
                  id="note"
                  {...register('note')}
                  rows={3}
                  placeholder={t('dialogs.customer.notePlaceholder', { ns: 'pages' })}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md shadow-sm focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:border-brand-500 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                />
              </FormField>

              <div className="flex justify-end space-x-3">
                <Button
                  type="submit"
                  className="bg-brand-600 hover:bg-brand-700 text-white font-bold py-2 px-4 rounded transition-all duration-200"
                >
                  {t('dialogs.customer.save', { ns: 'pages' })}
                </Button>
              </div>
            </form>

            {/* 歷史預約記錄 */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                {t('dialogs.customer.bookingHistory', { ns: 'pages' })}
              </h3>
              {customerDetail.bookings.length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400 text-center py-4">
                  {t('dialogs.customer.noBookings', { ns: 'pages' })}
                </p>
              ) : (
                <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-md overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('dialogs.customer.tableHeaders.bookingTime', { ns: 'pages' })}</TableHead>
                        <TableHead>{t('dialogs.customer.tableHeaders.serviceItem', { ns: 'pages' })}</TableHead>
                        <TableHead>{t('dialogs.customer.tableHeaders.staff', { ns: 'pages' })}</TableHead>
                        <TableHead>{t('dialogs.customer.tableHeaders.amount', { ns: 'pages' })}</TableHead>
                        <TableHead>{t('dialogs.customer.tableHeaders.status', { ns: 'pages' })}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {customerDetail.bookings.map((booking) => (
                        <TableRow key={booking.id}>
                          <TableCell>{formatDateTime(booking.date)}</TableCell>
                          <TableCell>{booking.service.name}</TableCell>
                          <TableCell>{booking.staff?.name || t('dialogs.customer.notAssigned', { ns: 'pages' })}</TableCell>
                          <TableCell>${Math.floor(Number(booking.price))}</TableCell>
                          <TableCell>{getStatusBadge(booking.status)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerDetailDialog;
